// Bibliotecas
import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Col, Row, Card, CardText, Form, CardBody } from "reactstrap";
import Dropzone from "react-dropzone";
import SweetAlert from "react-bootstrap-sweetalert";
import readXlsxFile from "read-excel-file";

// Assets
import ModeloUpload from "../../../assets/statics/modelo.xlsx";

// Services
import { createParticipant } from "../../../utils/services/participants";

// Utils
import { formatBytes } from "../../../utils/helpers/files/formatBytes";
import {
  allExcelColumns,
  excelColumnsPtBr,
  findRowsWithError,
  getUnregisteredEmails,
  getUnregisteredCpfs,
  verifyDuplicateCpfInDoc,
} from "./importParticipantsValidation";

// Componentes
import ModalExcelRows from "../Voo/ModalExcelRows";
import ModalRegisteredEmails from "./modalRegisteredParticipants";

const XLSX_SCHEMA = {
  nome: { prop: "name", type: String },
  email: { prop: "email", type: String },
  tituloQrCode: { prop: "titleQrCode", type: String },
  codigoQrCode: { prop: "codeQrCode", type: String },
  informacoesDeAcesso: { prop: "accessInfo", type: String },
  senha: { prop: "password", type: String },
  grupo: { prop: "group", type: String },
  cpf: { prop: "cpf", type: String },
  telefone: { prop: "phone", type: String },
  empresa: { prop: "company", type: String },
  cargo: { prop: "office", type: String },
  bioDoChat: { prop: "chat", type: String },
  codigoInterno1: { prop: "code1", type: String },
  codigoInterno2: { prop: "code2", type: String },
  codigoInterno3: { prop: "code3", type: String },
  restricaoHospedagem: { prop: "restricaoHospedagem", type: String },
};

function cpfFormat(cpf) {
  if (!cpf) return "";
  return cpf
    .replace(/[^\d]/g, "")
    .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function ImportParticipant(props) {
  const { isOpen, toggle, idEvent, active } = props;

  // state de arquivos
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imgError, setImgError] = useState("");

  // state de progresso
  const [totalRows, setTotalRows] = useState(0);
  const [totalSended, setTotalSended] = useState(0);
  const [btnSend, setBtnSend] = useState(false);
  const [btnCancel, setBtnCancel] = useState(false);

  // state de modais
  const [successDlg, setSuccessDlg] = useState(false);
  const [modalErrors, setModalErrors] = useState(false);
  const [errors, setErrors] = useState([]);
  const [modalRegisteredEmails, setModalRegisteredEmails] = useState(false);
  const [emails, setEmails] = useState([]);
  const [modalRegisteredCpfs, setModalRegisteredCpfs] = useState(false);
  const [modalRegisteredCpfsMessage, setModalRegisteredCpfsMessage] =
    useState("");
  const [cpfs, setCpfs] = useState([]);

  const importActivated = useRef(false);

  // Limpa o arquivo selecionado ao abrir/fechar o modal
  useEffect(() => {
    setSelectedFiles([]);
    setImgError("");
  }, [isOpen]);

  function toggleEmails() {
    setModalRegisteredEmails(!modalRegisteredEmails);
  }

  // Prepara o arquivo selecionado para exibição e habilita o botão de envio
  function handleAcceptedFiles(files) {
    files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      }),
    );
    setSelectedFiles(files);
    setTotalRows(0);
    setTotalSended(0);
    setBtnSend(true);
  }

  // Interrompe a importação em andamento e reseta os estados
  function cancelImport() {
    importActivated.current = false;
    active(importActivated.current);
    setSelectedFiles([]);
    setTotalRows(0);
    setTotalSended(0);
    setBtnSend(false);
    setBtnCancel(false);
  }

  // Lê o arquivo Excel, valida as linhas e envia os participantes sequencialmente
  async function uploadService() {
    importActivated.current = true;
    active(importActivated.current);
    setBtnSend(false);
    setBtnCancel(true);

    const importId = uuidv4();
    let count = 0;

    // Envia um participante e atualiza o contador de progresso
    async function importParticipants(row, index, total) {
      const result = await createParticipant(
        {
          email: row.email,
          nome: row.name,
          senha: row.password,
          foto: null,
          titleQrCode: row.titleQrCode,
          codeQrCode: row.codeQrCode,
          infoAcess: row.accessInfo,
          idEvento: idEvent,
          grupos: row.group,
          cpf: row.cpf,
          telefone: row.phone,
          empresa: row.company,
          cargo: row.office,
          interesseChat: row.chat,
          codigoInterno1: row.code1,
          codigoInterno2: row.code2,
          codigoInterno3: row.code3,
          restricaoHospedagem: row.restricaoHospedagem,
          importId,
        },
        props.token,
      );

      if (result.message === "SENDED_TO_QUEUE") {
        count += 1;
        setTotalSended(count);
      }
      if (total === index + 1) {
        setBtnCancel(false);
      }
      if (count === total) {
        setSuccessDlg(true);
        props.getAllParticipants();
      }
    }

    try {
      const { rows } = await readXlsxFile(selectedFiles[0], {
        schema: XLSX_SCHEMA,
      });
      const validateRows = rows.map((row, i) => allExcelColumns(row, i));

      const [rowsWithError, validRows] = await findRowsWithError(
        validateRows,
        props.token,
        props.idEvent,
      );

      const alreadyRegisteredEmails = await getUnregisteredEmails(
        validRows,
        props.token,
        props.idEvent,
      );

      const duplicateCpfInDoc = await verifyDuplicateCpfInDoc(rows);

      if (duplicateCpfInDoc.length > 0) {
        setCpfs(duplicateCpfInDoc);
        setModalRegisteredCpfsMessage(
          "O(s) seguinte(s) CPF(s) está(ão) duplicado(s) na planilha:",
        );
        setModalRegisteredCpfs(true);
        return;
      } else {
        setModalRegisteredCpfsMessage("");
        setCpfs([]);
      }

      const alreadyRegisteredCpfs = await getUnregisteredCpfs(
        props.token,
        props.idEvent,
        rows,
      );

      if (alreadyRegisteredCpfs.length > 0) {
        setCpfs(alreadyRegisteredCpfs);
        setModalRegisteredCpfsMessage(
          "O(s) seguinte(s) CPF(s) está(ão) em uso: ",
        );
        setTimeout(() => {
          setModalRegisteredCpfs(true);
        }, 500);
        return;
      } else {
        setModalRegisteredCpfsMessage("");
        setCpfs([]);
      }

      setEmails(alreadyRegisteredEmails);
      setTotalRows(validRows.length);

      if (rowsWithError.length > 0 || rows.length === 0) {
        setErrors(rowsWithError);
        importActivated.current = false;
        active(importActivated.current);
        setModalErrors(true);
        return;
      }

      let i = 0;
      for (const row of validRows) {
        await sleep(100);
        if (!importActivated.current) break;
        await importParticipants(row, i, validRows.length);
        i++;
      }
    } catch {
      setImgError(
        "Erro ao processar o arquivo. Verifique o formato e tente novamente.",
      );
      setBtnSend(true);
      setBtnCancel(false);
      importActivated.current = false;
      active(importActivated.current);
    }
  }

  return (
    <div>
      {successDlg && (
        <SweetAlert
          success
          title="Upload"
          onConfirm={() => {
            props.getAllParticipants();
            setSuccessDlg(false);
            if (emails.length > 0) {
              setModalRegisteredEmails(true);
            } else {
              importActivated.current = false;
              active(importActivated.current);
              toggle();
            }
          }}
        >
          O arquivo foi enviado com sucesso!
        </SweetAlert>
      )}
      {modalRegisteredCpfs && (
        <SweetAlert
          warning
          title="CPF(s) duplicados"
          onConfirm={() => setModalRegisteredCpfs(false)}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>{modalRegisteredCpfsMessage}</span>
            {cpfs.map((e, i) => (
              <span key={e}>
                {i + 1} - {cpfFormat(e)}
              </span>
            ))}
          </div>
        </SweetAlert>
      )}
      {modalErrors && (
        <ModalExcelRows
          toggle={() => setModalErrors(false)}
          rows={errors.sort((a, b) => a.index - b.index)}
          columnNames={excelColumnsPtBr}
        />
      )}
      <ModalRegisteredEmails
        isOpen={modalRegisteredEmails}
        toggle={toggleEmails}
        emails={emails}
        setEmails={setEmails}
      />
      <div className="m-1 p-0">
        <Card className="p-1">
          <CardBody className="m-1 p-2">
            <CardText className="mb-2 p-10">
              O arquivo a ser importado deve seguir este{" "}
              <a href={ModeloUpload} download="modelo.xlsx">
                Modelo
              </a>
              , não modifique seu cabeçalho.
            </CardText>

            <Form>
              {selectedFiles.length === 0 && (
                <Dropzone onDrop={handleAcceptedFiles}>
                  {({ getRootProps, getInputProps }) => (
                    <div className="dropzone">
                      <div
                        className="dz-message needsclick mt-1"
                        {...getRootProps()}
                      >
                        <input {...getInputProps()} />
                        <div className="mb-4">
                          <i className="display-4 text-muted bx bxs-cloud-upload" />
                          <h4>Arraste o arquivo ou clique aqui e selecione.</h4>
                        </div>
                      </div>
                    </div>
                  )}
                </Dropzone>
              )}
              <div className="dropzone-previews mt-0" id="file-previews">
                {selectedFiles.map((f) => (
                  <Card
                    className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                    key={f.name}
                  >
                    <div className="p-1">
                      <Row className="align-items-center">
                        <Col xs="11">
                          <strong>{`Nome: ${f.name} - Tamanho: ${f.formattedSize}`}</strong>
                        </Col>
                        <Col xs="1">
                          <span
                            style={{
                              fontSize: "16px",
                              color: "red",
                              fontWeight: "700",
                              cursor: "pointer",
                            }}
                            className="mdi mdi-close"
                            onClick={() => {
                              setSelectedFiles([]);
                              setBtnSend(false);
                            }}
                          />
                        </Col>
                      </Row>
                      {importActivated.current && (
                        <Row className="align-items-center">
                          <Col>
                            <strong>{`Total enviado: ${totalSended}/${totalRows}`}</strong>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </Form>

            <div className="text-center mt-3">
              <span style={{ color: "red" }}>{imgError}</span>
              <button
                type="button"
                className="btn btn-primary waves-effect waves-light"
                disabled={!btnSend}
                onClick={uploadService}
              >
                Enviar Participantes
              </button>
              <button
                type="button"
                className="btn btn-danger mx-1"
                disabled={!btnCancel}
                onClick={cancelImport}
              >
                Cancelar
              </button>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
